// Public /pricing page — unified rate sheet for every Xaluca hotel and
// bivouac. Each property gets its own card with a thumbnail + per-room
// table; a global comparison table at the bottom surfaces every row in
// one scrollable matrix for travel professionals.
//
// Filters: property (dropdown), accommodation type (chips), price range
// (min + max inputs in the currently displayed currency).

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BedDouble, Tent, Mountain, X as XClose } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import { usePricingForAllProperties } from "@/lib/usePricing";
import { FX_RATES, BOARD_BASIS_INCLUDED_LABELS, parseOccupancyBase, pickOccupancyLabel } from "@/lib/pricingDefaults";
import { useCurrency, useFormatPrice } from "@/contexts/CurrencyContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const CATEGORY_META = {
  hotel:   { icon: BedDouble, label: { en: "Hotels & Kasbahs", fr: "Hôtels & Kasbahs", es: "Hoteles y Kasbahs" } },
  bivouac: { icon: Tent,      label: { en: "Bivouacs & Camps", fr: "Bivouacs & Camps",  es: "Bivouacs y Camps" } },
  refuge:  { icon: Mountain,  label: { en: "Mountain Refuges", fr: "Refuges de montagne", es: "Refugios de montaña" } },
};

const COL_LABELS = {
  room: { en: "Room type", fr: "Type de chambre", es: "Tipo de habitación" },
  cap:  { en: "Occupancy", fr: "Occupation",     es: "Ocupación" },
  char: { en: "Characteristics", fr: "Caractéristiques", es: "Características" },
  property: { en: "Property", fr: "Propriété", es: "Propiedad" },
};

// Small helper — converts a per-room EUR value into a per-person figure by
// dividing by the row's occupancy. Returns null if the input is missing.
const perPerson = (eurPerRoom, occ) => {
  if (eurPerRoom == null || eurPerRoom === "") return null;
  const n = Number(eurPerRoom);
  if (!Number.isFinite(n)) return null;
  const o = Math.max(1, Number(occ) || 2);
  return Math.round((n / o) * 10) / 10;
};

const BOARD_KEYS = ["room_only", "bb", "hb", "fb"];

// In-EUR helper used by the range filter so the user can type rates in
// the currency shown on the page.
const eurFromDisplay = (displayValue, currency) => {
  const n = parseFloat(displayValue);
  if (Number.isNaN(n)) return null;
  return n / (FX_RATES[currency] || FX_RATES.EUR).rate;
};

const PricingPage = () => {
  const { lang, t } = useLanguage();
  const { byHotel, loading } = usePricingForAllProperties();
  // Global currency selector — same active currency across the entire site.
  const { currency, setCurrency } = useCurrency();
  const { fromEUR } = useFormatPrice();

  // ── Filter state ──────────────────────────────────────────────────────
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all"); // all / hotel / bivouac / refuge
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");

  const minEur = eurFromDisplay(priceMin, currency);
  const maxEur = eurFromDisplay(priceMax, currency);

  // Properties to render — coming-soon excluded, plus property/category
  // filters honoured. Order follows the HOTELS registry.
  const properties = useMemo(
    () =>
      HOTELS.filter((h) => !h.coming_soon)
        .filter((h) => (categoryFilter === "all" ? true : h.category === categoryFilter))
        .filter((h) => (propertyFilter === "all" ? true : h.slug === propertyFilter)),
    [propertyFilter, categoryFilter]
  );

  // Row-level price filter: keep a row only if AT LEAST one of the four
  // board-basis per-person prices falls within [minEur, maxEur].
  const rowPassesPriceFilter = (row) => {
    const occ = parseOccupancyBase(row.capacity);
    const pricesEur = BOARD_KEYS
      .map((k) => perPerson(row.prices ? row.prices[k] : null, occ))
      .filter((p) => p !== null && p !== undefined && p !== "");
    if (!pricesEur.length) return true;
    return pricesEur.some(
      (p) =>
        (minEur === null || p >= minEur) &&
        (maxEur === null || p <= maxEur)
    );
  };

  // Pre-compute filtered rows per property + a flat list for the global
  // comparison table.
  const filtered = useMemo(() => {
    const perProp = [];
    const flat = [];
    for (const h of properties) {
      const rows = (byHotel[h.slug] || []).filter(rowPassesPriceFilter);
      if (rows.length) {
        perProp.push({ hotel: h, rows });
        for (const r of rows) flat.push({ hotel: h, row: r });
      }
    }
    return { perProp, flat };
  }, [properties, byHotel, minEur, maxEur]);

  const resetFilters = () => {
    setPropertyFilter("all");
    setCategoryFilter("all");
    setPriceMin("");
    setPriceMax("");
  };

  const filtersDirty =
    propertyFilter !== "all" || categoryFilter !== "all" || priceMin || priceMax;

  return (
    <div className="bg-[#FBF6EE] text-[#2C2621]" data-testid="pricing-page">
      {/* ── HERO — multi-slide (video first, then property photography) ── */}
      <section data-testid="pricing-hero">
        <PageHero
          images={[
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
          ]}
          alt="Pricing · Grup Xaluca"
          scope="page.pricing"
          minHeightClass="min-h-[60vh] md:min-h-[70vh]"
          overline={pick({ en: "Pricing", fr: "Tarifs", es: "Tarifas" }, lang)}
          title={pick(
            {
              en: "Accommodation rates across the Xaluca collection",
              fr: "Tarifs d'hébergement de la collection Xaluca",
              es: "Tarifas de alojamiento de la colección Xaluca",
            },
            lang
          )}
          sub={pick(
            {
              en: "A single overview of every room across our hotels, kasbahs, refuges and desert camps. Switch currency, filter by property or budget, and download the full comparison.",
              fr: "Une vue unique sur chaque chambre de nos hôtels, kasbahs, refuges et camps. Changez de devise, filtrez par propriété ou budget, et téléchargez la comparaison.",
              es: "Una vista única de cada habitación de nuestros hoteles, kasbahs, refugios y campamentos. Cambia de divisa, filtra por propiedad o presupuesto y descarga la comparación.",
            },
            lang
          )}
        />
      </section>

      {/* ── FILTER BAR ───────────────────────────────────────────────── */}
      <section className="border-b border-[#2C2621]/10 bg-white sticky top-0 z-30" data-testid="pricing-filters">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-end gap-3 md:gap-5">
          {/* Property */}
          <label className="flex-1 min-w-[170px]">
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] block mb-1.5">
              {pick(COL_LABELS.property, lang)}
            </span>
            <select
              value={propertyFilter}
              onChange={(e) => setPropertyFilter(e.target.value)}
              className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#C16542]"
              data-testid="pricing-filter-property"
            >
              <option value="all">
                {pick({ en: "All properties", fr: "Toutes les propriétés", es: "Todas las propiedades" }, lang)}
              </option>
              {HOTELS.filter((h) => !h.coming_soon).map((h) => (
                <option key={h.slug} value={h.slug}>{h.name}</option>
              ))}
            </select>
          </label>

          {/* Category chips */}
          <div className="flex flex-wrap items-end gap-1.5">
            <span className="w-full text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1.5">
              {pick({ en: "Accommodation type", fr: "Type d'hébergement", es: "Tipo de alojamiento" }, lang)}
            </span>
            {[
              { key: "all", label: { en: "All", fr: "Tout", es: "Todo" } },
              { key: "hotel", label: CATEGORY_META.hotel.label },
              { key: "bivouac", label: CATEGORY_META.bivouac.label },
              { key: "refuge", label: CATEGORY_META.refuge.label },
            ].map((c) => {
              const isOn = c.key === categoryFilter;
              return (
                <button
                  type="button"
                  key={c.key}
                  onClick={() => setCategoryFilter(c.key)}
                  className={`px-3 py-2 text-[10px] tracking-[0.22em] uppercase border transition-colors ${
                    isOn
                      ? "bg-[#C16542] text-white border-[#C16542]"
                      : "bg-white text-[#2C2621] border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542]"
                  }`}
                  data-testid={`pricing-filter-category-${c.key}`}
                >
                  {pick(c.label, lang)}
                </button>
              );
            })}
          </div>

          {/* Price range */}
          <div className="flex items-end gap-2">
            <label>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] block mb-1.5">
                {pick({ en: "Min", fr: "Min", es: "Mín" }, lang)}
              </span>
              <input
                type="number"
                inputMode="decimal"
                placeholder="0"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                className="w-24 border border-[#2C2621]/15 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#C16542]"
                data-testid="pricing-filter-min"
              />
            </label>
            <label>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] block mb-1.5">
                {pick({ en: "Max", fr: "Max", es: "Máx" }, lang)}
              </span>
              <input
                type="number"
                inputMode="decimal"
                placeholder="∞"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                className="w-24 border border-[#2C2621]/15 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#C16542]"
                data-testid="pricing-filter-max"
              />
            </label>
          </div>

          {/* Currency */}
          <div className="flex items-end gap-1.5">
            <span className="w-full text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1.5">
              {pick({ en: "Currency", fr: "Devise", es: "Divisa" }, lang)}
            </span>
            {Object.values(FX_RATES).map((c) => {
              const isOn = currency === c.code;
              return (
                <button
                  type="button"
                  key={c.code}
                  onClick={() => setCurrency(c.code)}
                  className={`px-3 py-2 text-[10px] tracking-[0.22em] uppercase border transition-colors ${
                    isOn
                      ? "bg-[#2C2621] text-white border-[#2C2621]"
                      : "bg-white text-[#2C2621] border-[#2C2621]/15 hover:border-[#2C2621]/40"
                  }`}
                  data-testid={`pricing-currency-${c.code}`}
                >
                  {c.code}
                </button>
              );
            })}
          </div>

          {filtersDirty ? (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[#C16542] hover:text-[#1A1513]"
              data-testid="pricing-filter-reset"
            >
              <XClose className="w-3.5 h-3.5" /> {pick({ en: "Reset", fr: "Réinitialiser", es: "Restablecer" }, lang)}
            </button>
          ) : null}
        </div>
      </section>

      {/* ── PER-PROPERTY SECTIONS ────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 space-y-12 md:space-y-16" data-testid="pricing-per-property">
        {loading ? (
          <p className="text-sm text-[#5C5248] italic">
            {lang === "es" ? "Cargando tarifas…" : lang === "fr" ? "Chargement des tarifs…" : "Loading rates…"}
          </p>
        ) : filtered.perProp.length === 0 ? (
          <p className="text-sm text-[#5C5248] italic" data-testid="pricing-empty-state">
            {pick(
              {
                en: "No rooms match the current filters.",
                fr: "Aucune chambre ne correspond aux filtres en cours.",
                es: "Ninguna habitación coincide con los filtros actuales.",
              },
              lang
            )}
          </p>
        ) : (
          filtered.perProp.map(({ hotel, rows }) => {
            const Cat = (CATEGORY_META[hotel.category] || CATEGORY_META.hotel).icon;
            return (
              <article
                key={hotel.slug}
                id={`pricing-${hotel.slug}`}
                className="bg-white border border-[#2C2621]/10 overflow-hidden"
                data-testid={`pricing-property-${hotel.slug}`}
                data-cms-scope={`pricing-property.${hotel.slug}`}
              >
                <header className="grid sm:grid-cols-[200px_1fr] gap-0 border-b border-[#2C2621]/10">
                  <div className="relative aspect-[4/3] sm:aspect-auto sm:h-full bg-[#1A1513]">
                    <img
                      src={hotel.image}
                      alt={hotel.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      data-no-xaluca-logo="true" loading="lazy" decoding="async"
                      data-cms-role="thumb"/>
                  </div>
                  <div className="p-5 md:p-7 flex flex-col">
                    <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C16542]">
                      <Cat className="w-3 h-3" strokeWidth={1.8} />
                      {pick((CATEGORY_META[hotel.category] || CATEGORY_META.hotel).label, lang)}
                    </span>
                    <h3 className="font-serif-x text-2xl md:text-3xl tracking-tight mt-1.5">
                      {hotel.name}
                    </h3>
                    <p className="text-sm text-[#5C5248] mt-1">{hotel.location}</p>
                    <div className="mt-auto pt-4">
                      <Link
                        to={`/hotels/${hotel.slug}`}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[#C16542] hover:gap-2 transition-all"
                        data-testid={`pricing-view-${hotel.slug}`}
                      >
                        {pick({ en: "View property", fr: "Voir la propriété", es: "Ver propiedad" }, lang)}
                        <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
                      </Link>
                    </div>
                  </div>
                </header>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[680px] text-sm" data-testid={`pricing-table-${hotel.slug}`}>
                    <thead className="bg-[#FBF6EE] text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
                      <tr>
                        <th className="text-left px-5 py-3">{pick(COL_LABELS.room, lang)}</th>
                        <th className="text-left px-5 py-3">{pick(COL_LABELS.cap, lang)}</th>
                        <th className="text-left px-5 py-3 hidden md:table-cell">{pick(COL_LABELS.char, lang)}</th>
                        {BOARD_KEYS.map((k) => (
                          <th key={k} className="text-right px-5 py-3 whitespace-nowrap">
                            {pick(BOARD_BASIS_INCLUDED_LABELS[k], lang)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const occ = parseOccupancyBase(r.capacity);
                        return (
                          <tr key={r.id} className="border-t border-[#2C2621]/8 hover:bg-[#FBF6EE]/40 transition-colors">
                            <td className="px-5 py-3.5 font-medium text-[#1A1513]">{pick(r.room_name, lang)}</td>
                            <td className="px-5 py-3.5 text-[#5C5248] whitespace-nowrap">{pickOccupancyLabel(occ, lang)}</td>
                            <td className="px-5 py-3.5 text-[#5C5248] hidden md:table-cell">
                              {pick(r.characteristics, lang) || "—"}
                            </td>
                            {BOARD_KEYS.map((k) => (
                              <td
                                key={k}
                                className="px-5 py-3.5 text-right font-medium text-[#1A1513] whitespace-nowrap"
                                data-testid={`pricing-${hotel.slug}-${r.id}-${k}`}
                              >
                                {fromEUR(perPerson(r.prices?.[k], occ))}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <footer className="px-5 py-3 text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/80 border-t border-[#2C2621]/8 bg-[#FBF6EE]/40">
                  {pick(
                    {
                      en: "Per person · per night · taxes included · subject to availability",
                      fr: "Par personne · par nuit · taxes incluses · sous réserve de disponibilité",
                      es: "Por persona · por noche · impuestos incluidos · sujeto a disponibilidad",
                    },
                    lang
                  )}
                </footer>
              </article>
            );
          })
        )}
      </section>

      {/* ── GLOBAL COMPARISON TABLE ──────────────────────────────────── */}
      <section className="bg-[#1A1513] text-[#FDFBF7] py-14 md:py-20 border-t border-[#D4A373]/15" data-testid="pricing-comparison">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <p className="overline !text-[#D4A373]">
                {pick({ en: "Comparison", fr: "Comparaison", es: "Comparativa" }, lang)}
              </p>
              <h2 className="font-serif-x text-3xl md:text-4xl mt-2 tracking-tight">
                {pick(
                  { en: "All rooms · one matrix", fr: "Toutes les chambres · une matrice", es: "Todas las habitaciones · una matriz" },
                  lang
                )}
              </h2>
            </div>
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/55" data-testid="pricing-comparison-count">
              {filtered.flat.length} {pick({ en: "rows", fr: "lignes", es: "filas" }, lang)}
            </span>
          </div>
          <div className="overflow-x-auto bg-[#221813] border border-[#D4A373]/20" data-testid="pricing-comparison-wrap">
            <table className="w-full min-w-[840px] text-sm" data-testid="pricing-comparison-table">
              <thead className="bg-[#1A1513] text-[10px] tracking-[0.22em] uppercase text-[#D4A373]">
                <tr>
                  <th className="text-left px-5 py-3">{pick(COL_LABELS.property, lang)}</th>
                  <th className="text-left px-5 py-3">{pick(COL_LABELS.room, lang)}</th>
                  <th className="text-left px-5 py-3">{pick(COL_LABELS.cap, lang)}</th>
                  <th className="text-left px-5 py-3 hidden lg:table-cell">{pick(COL_LABELS.char, lang)}</th>
                  {BOARD_KEYS.map((k) => (
                    <th key={k} className="text-right px-5 py-3 whitespace-nowrap">
                      {pick(BOARD_BASIS_INCLUDED_LABELS[k], lang)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.flat.length === 0 ? (
                  <tr>
                    <td colSpan={4 + BOARD_KEYS.length} className="px-5 py-8 text-center text-[#FDFBF7]/50 italic">
                      {pick({ en: "No rows match the filters.", fr: "Aucune ligne ne correspond.", es: "Sin coincidencias." }, lang)}
                    </td>
                  </tr>
                ) : (
                  filtered.flat.map(({ hotel, row }) => {
                    const occ = parseOccupancyBase(row.capacity);
                    return (
                      <tr key={`${hotel.slug}-${row.id}`} className="border-t border-[#FDFBF7]/8 hover:bg-[#FDFBF7]/4 transition-colors">
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <Link to={`/hotels/${hotel.slug}`} className="text-[#FDFBF7] hover:text-[#D4A373] transition-colors">
                            {hotel.name}
                          </Link>
                        </td>
                        <td className="px-5 py-3.5 font-medium">{pick(row.room_name, lang)}</td>
                        <td className="px-5 py-3.5 text-[#FDFBF7]/75 whitespace-nowrap">{pickOccupancyLabel(occ, lang)}</td>
                        <td className="px-5 py-3.5 text-[#FDFBF7]/65 hidden lg:table-cell">
                          {pick(row.characteristics, lang) || "—"}
                        </td>
                        {BOARD_KEYS.map((k) => (
                          <td
                            key={k}
                            className="px-5 py-3.5 text-right whitespace-nowrap font-medium"
                            data-testid={`comparison-${hotel.slug}-${row.id}-${k}`}
                          >
                            {fromEUR(perPerson(row.prices?.[k], occ))}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
